<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query";
import { ModK } from "ls/common/keyboardShortcuts";
import { useKeyboardShortcut } from "ls/common/useKeyboardShortcut";
import { CommandDialog, CommandInput, CommandList } from "ls/components/command";
import { SimpleSwitch } from "ls/components/simple-switch";
import { ArrowUpDownIcon, CornerDownLeftIcon } from "lucide-vue-next";
import { storeToRefs } from "pinia";
import { computed, ref, shallowRef, watch } from "vue";
import { queries as recentsQueries } from "../recents/queries";
import CommandPageRecents from "./CommandPageRecents.vue";
import CommandPageSearch from "./CommandPageSearch.vue";
import { queries } from "./queries";
import { useCommandKControls, useSearchStore } from "./useCommandKControls";
import { useDeepSearchAllowed } from "./useDeepSearchAllowed";

const { isOpen } = useCommandKControls();

const searchStore = useSearchStore();
const { nameOnly } = storeToRefs(searchStore);

const { deepSearchAllowed } = useDeepSearchAllowed();

// remember the active element before opening the dialog
// so that it can be focused again when the dialog is closed
const savedActiveElement = shallowRef<Element>();
const savedSelectionRange = ref<Range>();

function saveFocus() {
  savedActiveElement.value = document.activeElement ?? undefined;
  const selection = window.getSelection();
  if (selection && selection.rangeCount > 0) {
    savedSelectionRange.value = selection.getRangeAt(0);
  }
}

// Function to restore the saved selection
function restoreFocus() {
  if (savedActiveElement.value instanceof HTMLElement) {
    savedActiveElement.value.focus();
  }
  if (savedSelectionRange.value) {
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(savedSelectionRange.value);
    }
  }
}

useKeyboardShortcut(ModK, event => {
  saveFocus();
  event.preventDefault();
  isOpen.value = true;
});

const searchTerm = ref("");
const shouldSearch = computed(() => searchTerm.value.trim().length > 0);

const queryClient = useQueryClient();
void queryClient.prefetchQuery(queries.userSearchData());
void queryClient.prefetchQuery(recentsQueries.recents());

watch(isOpen, value => {
  const isClosed = !value;
  if (isClosed) {
    searchTerm.value = "";
    restoreFocus();
  }
}, {
  // use flush 'post' to restore original focus only after the dialog is closed and removed from DOM
  // otherwise the focus will remain within Dialog focus trap
  flush: "post",
});
</script>

<template>
  <CommandDialog
    v-model:open="isOpen"
    title="Search"
    description="Search workspaces, folders and files across all your organizations"
  >
    <CommandInput v-model="searchTerm" placeholder="Search" @close="isOpen = false">
      <template v-if="deepSearchAllowed" #control>
        <label for="name-only" class="text-xs text-slate-500 select-none">
          Name-only search
        </label>
        <SimpleSwitch
          id="name-only"
          :model-value="nameOnly"
          @update:model-value="(val: boolean) => nameOnly = val"
        />
      </template>
    </CommandInput>
    <CommandList @escape-key-down="isOpen = false">
      <CommandPageSearch
        v-if="shouldSearch"
        @select="isOpen = false"
      />
      <CommandPageRecents
        v-if="!shouldSearch"
        @select="isOpen = false"
      />
    </CommandList>
    <div class="flex gap-4 border-t border-slate-200 px-3 py-1 text-xs/6 text-slate-500 select-none">
      <div class="flex items-center gap-1">
        Select
        <kbd>
          <ArrowUpDownIcon class="size-3" aria-hidden="true" />
          <span class="sr-only">Arrow Up Arrow Down</span>
        </kbd>
      </div>
      <div class="flex items-center gap-1">
        Open
        <kbd>
          <CornerDownLeftIcon class="size-3" aria-hidden="true" />
          <span class="sr-only">Enter</span>
        </kbd>
      </div>
      <div class="ml-auto flex items-center gap-1">
        Search <kbd>{{ ModK.label }}</kbd>
      </div>
    </div>
  </CommandDialog>
</template>
